import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

import { TabRewardsPng } from '../../../assets/event';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { EVENT_FONT, eventColors, eventGlow, GOLD_RAMP, groupThousands } from './eventTheme';

// Figma component "Event Progress / Milestone Rail" (3119:12881 / 3169:14415): 344x44, a 3px
// muted-gold track with a gold-gradient fill running to the current dot, milestone labels on
// top and 24x24 "Milestone Dot" instances (3116:12044) on the line.
//
// The dots are drawn here rather than imported as the exported SVGs: SVGR hardcodes the gradient
// ids, and a rail paints six or seven of them, so on web every copy would resolve to the first
// one in the document (see useSvgId). The paths and stops below are the exported artwork's.
export const RAIL_DESIGN_HEIGHT = 44;
const DOT_DESIGN_SIZE = 24;
/** The circle inside the 24x24 dot box: 15x15 at 4.5,4.5 for claimed / locked. */
const DOT_CIRCLE_INSET = 4.5;

export type MilestoneState = 'reached' | 'current' | 'locked';

export const milestoneStateAt = (index: number, currentIndex: number): MilestoneState =>
	index < currentIndex ? 'reached' : index === currentIndex ? 'current' : 'locked';

type Props = {
	milestones: number[];
	currentIndex: number;
	/** Centre of each milestone, in viewport px from the rail's left edge. */
	centers: number[];
	scale: number;
};

/** Claimed: the gold ramp in a 15px circle under a cream check. */
const ClaimedDot = ({ size }: { size: number }) => {
	const fillId = useSvgId('eventDotClaimed');
	const strokeId = useSvgId('eventDotClaimedStroke');

	return (
		<Svg width={size} height={size} viewBox='0 0 15 15'>
			<Defs>
				<LinearGradient id={fillId} x1='7.5' y1='0' x2='7.5' y2='15' gradientUnits='userSpaceOnUse'>
					{GOLD_RAMP.map(([offset, color]) => (
						<Stop key={offset} offset={offset} stopColor={color} />
					))}
				</LinearGradient>
				<LinearGradient
					id={strokeId}
					x1='7.5'
					y1='15'
					x2='7.5'
					y2='0'
					gradientUnits='userSpaceOnUse'
				>
					<Stop offset='0' stopColor='#FFF908' />
					<Stop offset='1' stopColor='#FFFF9E' />
				</LinearGradient>
			</Defs>
			<Circle cx='7.5' cy='7.5' r='6.8' fill={`url(#${fillId})`} stroke={`url(#${strokeId})`} />
			<Path
				d='M10.6832 5.13112C10.3434 4.90526 9.87105 4.97847 9.62832 5.29468L6.73559 9.06262L5.38318 7.19658C5.14973 6.8744 4.6797 6.78936 4.33346 7.00659C3.98721 7.22382 3.89583 7.6612 4.12928 7.98339L6.0908 10.6899C6.22929 10.8811 6.45974 10.9969 6.70761 11C6.71103 11 6.71446 11 6.71789 11C6.9619 11 7.19106 10.8904 7.33313 10.7053L10.8591 6.11257C11.1018 5.79636 11.023 5.35699 10.6832 5.13112Z'
				fill='#FFE9A8'
			/>
		</Svg>
	);
};

/** Locked: a dark disc with the olive rim and a translucent gold padlock. */
const LockedDot = ({ size }: { size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 15 15'>
		<Circle
			cx='7.5'
			cy='7.5'
			r='6.9'
			fill={eventColors.lockedNode}
			stroke={eventColors.muted}
			strokeWidth='0.653'
		/>
		<Path
			d='M5.85728 6.57143V5.71429C5.85728 5.25963 6.03789 4.82359 6.35939 4.5021C6.68088 4.18061 7.11691 4 7.57157 4C8.02623 4 8.46226 4.18061 8.78375 4.5021C9.10524 4.82359 9.28585 5.25963 9.28585 5.71429V6.57143'
			stroke={eventColors.accent}
			strokeWidth='1'
			fill='none'
			opacity='0.55'
		/>
		<Path
			d='M9.28585 5.68251H5.85728C5.3839 5.68251 5.00014 6.06626 5.00014 6.53965V9.11108C5.00014 9.58447 5.3839 9.96822 5.85728 9.96822H9.28585C9.75924 9.96822 10.143 9.58447 10.143 9.11108V6.53965C10.143 6.06626 9.75924 5.68251 9.28585 5.68251Z'
			fill={eventColors.accent}
			opacity='0.55'
		/>
		<Path
			d='M7.5 7.03966V8.75395'
			stroke={eventColors.surface}
			strokeWidth='1.28571'
			opacity='0.55'
		/>
	</Svg>
);

/** Available: the gold disc with the reward gift sitting on it, and the bloom around both. */
const AvailableDot = ({ size }: { size: number }) => {
	const fillId = useSvgId('eventDotAvailable');

	return (
		<View style={{ width: size, height: size }}>
			<Svg width={size} height={size} viewBox='0 0 24 24'>
				<Defs>
					<LinearGradient id={fillId} x1='12' y1='0' x2='12' y2='24' gradientUnits='userSpaceOnUse'>
						{GOLD_RAMP.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Circle
					cx='12'
					cy='12'
					r='11.2'
					fill={`url(#${fillId})`}
					stroke={eventColors.panelBorderGold}
				/>
			</Svg>
			<Image
				source={TabRewardsPng}
				resizeMode='contain'
				style={{
					position: 'absolute',
					left: size * 0.22,
					top: size * 0.22,
					width: size * 0.56,
					height: size * 0.56,
				}}
			/>
		</View>
	);
};

export const MilestoneRail = ({ milestones, currentIndex, centers, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const trackId = useSvgId('eventRailFill');
	// The dot row is centred 32 down the 44-tall strip, leaving the labels the top 16.
	const nodeCenterY = 32 * scale;
	const dotSize = DOT_DESIGN_SIZE * scale;

	const first = centers[0] ?? 0;
	const last = centers[centers.length - 1] ?? 0;
	const reachedTo = centers[Math.min(currentIndex, centers.length - 1)] ?? first;
	const fillWidth = Math.max(0, reachedTo - first);

	return (
		<View style={styles.rail}>
			{milestones.map((points, index) => {
				const state = milestoneStateAt(index, currentIndex);
				return (
					<Text
						key={`label-${points}-${index}`}
						numberOfLines={1}
						style={[
							styles.label,
							state === 'locked'
								? styles.labelLocked
								: state === 'current'
									? styles.labelCurrent
									: styles.labelReached,
							{ left: centers[index] - 27 * scale },
						]}
					>
						{groupThousands(points)}
					</Text>
				);
			})}

			<View
				style={[
					styles.track,
					{ top: nodeCenterY - 1.5 * scale, left: first, width: Math.max(0, last - first) },
				]}
			/>
			{fillWidth > 0 ? (
				<View
					style={[
						styles.trackFill,
						{ top: nodeCenterY - 1.5 * scale, left: first, width: fillWidth },
					]}
				>
					<Svg width='100%' height='100%'>
						<Defs>
							<LinearGradient id={trackId} x1='0' y1='0' x2='0' y2='1'>
								{GOLD_RAMP.map(([offset, color]) => (
									<Stop key={offset} offset={offset} stopColor={color} />
								))}
							</LinearGradient>
						</Defs>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${trackId})`} />
					</Svg>
				</View>
			) : null}

			{milestones.map((points, index) => {
				const state = milestoneStateAt(index, currentIndex);
				const inset = state === 'current' ? 0 : DOT_CIRCLE_INSET * scale;

				return (
					<View
						key={`dot-${points}-${index}`}
						style={[
							styles.dot,
							{
								width: dotSize,
								height: dotSize,
								left: centers[index] - dotSize / 2,
								top: nodeCenterY - dotSize / 2,
								padding: inset,
							},
							state === 'current' && [{ borderRadius: dotSize / 2 }, eventGlow.bloom],
						]}
					>
						{state === 'reached' ? (
							<ClaimedDot size={dotSize - inset * 2} />
						) : state === 'current' ? (
							<AvailableDot size={dotSize} />
						) : (
							<LockedDot size={dotSize - inset * 2} />
						)}
					</View>
				);
			})}
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		rail: {
			width: '100%',
			height: RAIL_DESIGN_HEIGHT * scale,
		},
		// Figma: Sofia Sans Medium 12 / 16, centred on a 54-wide column.
		label: {
			position: 'absolute',
			top: 0,
			width: 54 * scale,
			textAlign: 'center',
			fontFamily: EVENT_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
			lineHeight: 16 * scale,
		},
		labelReached: { color: eventColors.goldSoft },
		labelCurrent: { color: eventColors.accent },
		labelLocked: { color: eventColors.muted },
		track: {
			position: 'absolute',
			height: 3 * scale,
			borderRadius: 3 * scale,
			backgroundColor: eventColors.muted,
		},
		trackFill: {
			position: 'absolute',
			height: 3 * scale,
			borderRadius: 3 * scale,
			overflow: 'hidden',
		},
		dot: {
			position: 'absolute',
			alignItems: 'center',
			justifyContent: 'center',
		},
	});

export default MilestoneRail;
